import { supabase } from '../lib/supabase';
import { captureAppException, createSupabaseError } from './errorCaptureUtils';

/**
 * Fetches user profile data with retry logic to handle race conditions.
 * 
 * RACE CONDITION HANDLING:
 * This function includes a retry mechanism to handle a race condition where:
 * 1. OAuth completes and creates an auth.users record
 * 2. AuthContext immediately provides the user object
 * 3. This function is called before the database trigger (handle_new_user) completes
 * 4. The trigger creates the public.users record asynchronously
 * 
 * We retry with short delays (100ms, 500ms) because:
 * - Database triggers typically complete within milliseconds
 * - 600ms total is more than sufficient for trigger completion
 * - Excessive retries would indicate a real problem, not a race condition
 * - We want to fail fast for actual errors (missing user, permissions, etc.)
 * 
 * @param userId The ID of the user to fetch
 * @param context Context for error reporting (e.g., 'checkUserProfileCompleted', 'oauth_profile_fetch')
 * @returns Promise resolving to the user data or null if not found after retries
 */
export async function fetchUserWithRetry(userId: string, context: string): Promise<any> {
  const retryDelays = [100, 500]; // 100ms, then 500ms retry delays
  const maxAttempts = 3; // Initial attempt + 2 retries

  for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex++) {
    // Add delay for retry attempts (skip delay for first attempt)
    if (attemptIndex > 0) {
      await new Promise(resolve => setTimeout(resolve, retryDelays[attemptIndex - 1]));
    }

    const { data, error } = await supabase
      .from('users')
      .select('profile_completed')
      .eq('id', userId)
      .single();

    if (error) {
      const isNoRowsError = error.code === 'PGRST116';
      const isNotFinalAttempt = attemptIndex < maxAttempts - 1;
      
      // Only retry for PGRST116 (no rows) errors, and only if we haven't reached final attempt
      if (isNoRowsError && isNotFinalAttempt) {
        continue; // Retry
      }
      
      // For other errors or final attempt - capture error and return null
      const errorToCapture = error?.code && error?.message ? createSupabaseError(error, context) : error;
      captureAppException(errorToCapture, {
        tags: {
          context: `${context}_util`,
          user_id: userId,
          attempt: attemptIndex + 1,
          total_attempts: maxAttempts,
          final_attempt: !isNotFinalAttempt,
          error_code: error.code
        },
      });
      return null;
    }

    // Success - return the user data
    return data;
  }

  // This should never be reached, but just in case
  return null;
}

/**
 * Checks if a user's profile is marked as completed in the database.
 * Uses retry logic to handle race conditions between OAuth and database triggers.
 * 
 * @param userId The ID of the user to check.
 * @returns True if the profile is complete, false otherwise (including on error).
 */
export const checkUserProfileCompleted = async (userId: string): Promise<boolean> => {
  if (!userId) {
    console.warn('checkUserProfileCompleted called with no userId');
    return false;
  }

  const userData = await fetchUserWithRetry(userId, 'checkUserProfileCompleted');
  return userData?.profile_completed ?? false;
}; 